import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';
import { reduxForm, formValueSelector, getFormSyncErrors } from 'redux-form';
import type { InjectedFormProps } from 'redux-form';
import type { FormState } from 'redux-form';

import { AccountPermissionTemplate } from '@acceligentllc/shared/enums/accountPermissionTemplate';

import type { RootState } from 'af-reducers';

import { MEMBER_EDIT } from 'af-constants/reduxForms';
import CLIENT from 'af-constants/routes/client';

import Breadcrumbs from 'af-components/Breadcrumbs';

import * as AccountActions from 'af-actions/accounts';
import * as CompanyActions from 'af-actions/companies';
import * as LocationActions from 'af-actions/location';

import { validate } from './validations';
import MemberForm from '../Form';
import FormModel from '../Form/formModel';
import MemberInfo from '../Form/UserInfo';

interface PathParams {
	memberId: string;
}

type OwnProps = CustomRouteComponentProps<PathParams>;

type FormOwnProps = OwnProps & ConnectedProps<typeof connector>;

type Props = FormOwnProps & InjectedFormProps<FormModel>;

interface State {
	image: File | string | null;
	imageUrl: string | null;
	deleteImage: boolean;
}

class CompanyMemberEdit extends React.PureComponent<Props, State> {
	state: State = {
		image: null,
		imageUrl: null,
		deleteImage: false,
	};

	async componentDidMount() {
		const { getMember, match: { params: { memberId } }, initialize, getCompany } = this.props;
		const form = await getMember(+memberId);

		if (!form) {
			throw new Error('Unable to initialize form');
		}

		await getCompany();

		this.setState(() => ({ imageUrl: form.user.imageUrl }));
		initialize(FormModel.fromViewModel(form));
	}

	submit = async (data: FormModel) => {
		const { editMember } = this.props;
		const { deleteImage, image } = this.state;

		const form = FormModel.toEditRM(data);
		form.imageUrl = deleteImage ? null : image as string;
		form.deleteImage = deleteImage;
		await editMember(form);
		this.goBack();
	};

	goBack = () => {
		const { location: { state: { orgAlias } }, companyName, history } = this.props;
		history.push(CLIENT.COMPANY.SETTINGS.MEMBERS.TABLE(orgAlias, companyName));
	};

	deleteImage = () => {
		this.setState(() => ({ image: null, imageUrl: null, deleteImage: true }));
	};

	onImageUploadSave = (image: File, imageUrl: string) => {
		this.setState(() => ({
			image,
			imageUrl,
			deleteImage: false,
		}));
	};

	render() {
		const {
			handleSubmit,
			submitting,
			change,
			invalid,
			offlinePin,
			companyName,
			subscriptionStatus,
			isCompanyAdmin,
			permissions,
			accountTemplate,
			assignableToWorkOrder,
			assignableAsSuperintendent,
			assignableAsAccounting,
			assignableAsManagement,
			assignableAsTechnician,
			findAllLocations,
			location: { state: { orgAlias } },
			invalidEmail,
			invalidPhoneNumber,
			activeInLMS,
			excludeFromNotifyAll,
			companyWaiverSetting,
			waiverStatusValidFrom,
			initialWaiverStatusValidFrom,
			timeRoundingPolicyFrom,
		} = this.props;
		const { image, imageUrl } = this.state;

		return (
			<div className="form-segment">
				<Breadcrumbs
					items={
						[
							{ label: 'Users', url: CLIENT.COMPANY.SETTINGS.MEMBERS.TABLE(orgAlias, companyName) },
							{ label: 'Edit' },
						]
					}
				/>
				<MemberForm
					accountTemplate={accountTemplate}
					activeInLMS={activeInLMS}
					assignableAsAccounting={assignableAsAccounting}
					assignableAsManagement={assignableAsManagement}
					assignableAsSuperintendent={assignableAsSuperintendent}
					assignableAsTechnician={assignableAsTechnician}
					assignableToWorkOrder={assignableToWorkOrder}
					change={change}
					companyWaiverSetting={companyWaiverSetting}
					excludeFromNotifyAll={excludeFromNotifyAll}
					initialWaiverStatusValidFrom={initialWaiverStatusValidFrom}
					invalid={invalid}
					invalidEmail={invalidEmail}
					invalidPhoneNumber={invalidPhoneNumber}
					isCompanyAdmin={isCompanyAdmin}
					onCancel={this.goBack}
					onSubmit={handleSubmit(this.submit)}
					permissions={permissions}
					showActivateImmediately={false}
					submitting={submitting}
					timeRoundingPolicyFrom={timeRoundingPolicyFrom}
					waiverStatusValidFrom={waiverStatusValidFrom}
				>
					<MemberInfo
						deleteImage={this.deleteImage}
						image={image}
						imageUrl={imageUrl}
						isEmailInvalid={invalidEmail}
						isPhoneNumberInvalid={invalidPhoneNumber}
						loadLocations={findAllLocations}
						offlinePin={offlinePin}
						onImageUploadSave={this.onImageUploadSave}
						subscriptionStatus={subscriptionStatus}
					/>
				</MemberForm>
			</div>
		);
	}

}

type FormStateWithInitial = FormState & {
	initial: FormState['values'];
};

const selector = formValueSelector(MEMBER_EDIT);
const errorSelector = getFormSyncErrors(MEMBER_EDIT);

function mapStateToProps(state: RootState) {
	const { user: { companyData }, company: { company } } = state;
	if (!companyData) {
		throw new Error('User not logged in');
	}

	const formState = state.form[MEMBER_EDIT] as FormStateWithInitial;
	const {
		subscriptionStatus,
		permissions,
		offlinePin = null,
		accountTemplateId,
		assignableToWorkOrder,
		assignableAsSuperintendent,
		assignableAsAccounting,
		assignableAsManagement,
		assignableAsTechnician,
		user,
		existingUser,
		waiverStatusValidFrom,
	} = selector(
		state,
		'subscriptionStatus',
		'permissions',
		'offlinePin',
		'accountTemplateId',
		'assignableToWorkOrder',
		'assignableAsSuperintendent',
		'assignableAsAccounting',
		'assignableAsManagement',
		'assignableAsTechnician',
		'user',
		'existingUser',
		'waiverStatusValidFrom'
	);

	const phoneNumber = user?.phoneNumber ?? existingUser?.phoneNumber;
	const email = user?.email ?? existingUser?.email;
	const { user: userErrors } = errorSelector(state) as FormModel;
	return {
		companyName: companyData.name,
		companyWaiverSetting: company?.companyWaiverStatus ?? false,
		timeRoundingPolicyFrom: company?.timeRoundingPolicyFrom ?? null,
		offlinePin,
		isCompanyAdmin: accountTemplateId === AccountPermissionTemplate.ADMIN,
		subscriptionStatus,
		accountTemplate: accountTemplateId,
		permissions,
		assignableToWorkOrder,
		assignableAsSuperintendent,
		assignableAsAccounting,
		assignableAsManagement,
		assignableAsTechnician,
		invalidEmail: !email || !!userErrors?.email,
		invalidPhoneNumber: !phoneNumber || !!userErrors?.phoneNumber,
		activeInLMS: user?.activeInLMS,
		excludeFromNotifyAll: user?.excludeFromNotifyAll,
		waiverStatusValidFrom,
		initialWaiverStatusValidFrom: formState?.initial?.waiverStatusValidFrom ?? null,
	};
}

function mapDispatchToProps() {
	return {
		findAllLocations: LocationActions.findList,
		getMember: AccountActions.findById,
		editMember: AccountActions.update,
		getCompany: CompanyActions.getCompany,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(
	connector,
	reduxForm<FormModel, FormOwnProps>({ form: MEMBER_EDIT, validate })
);

export default enhance(CompanyMemberEdit);
